import { AssistantPageState } from '../assistantContextTypes';
import { getPageDescriptor } from '../assistantPageRegistry';

/**
 * Income Journal detail → assistant page state.
 *
 * Publishes ONLY what the user can see or has selected: property, the loaded
 * date range, the category filter, the display currency, and the selected
 * business date.
 *
 * Conspicuously absent: the balance. The page has `net`, `creditTotal`,
 * `debitTotal` and `unmappedAmount` in state and it would be trivial to attach
 * them — which is exactly why this is worth a comment. An imbalance is a
 * financial figure; sending one as "context" would let a stale render decide
 * what the assistant believes. The server re-reads the journal and computes it.
 *
 * A pure function so the page's own React state stays private to the page and
 * this mapping can be tested without rendering anything.
 */

export interface JournalDetailContextInput {
  orgId: number | null | undefined;
  locationId: number | null | undefined;
  locationName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  /**
   * The business date the user has focused, if the page tracks one. When it
   * does not, a single-day range is itself the selection — that is the shape
   * of "I am looking at this day".
   */
  selectedDate?: string | null;
  categories?: string[] | null;
  displayCurrency?: string | null;
  route?: string | null;
}

function toInt(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isInteger(n) ? n : null;
}

export function buildJournalDetailContext(input: JournalDetailContextInput): AssistantPageState | null {
  const orgId = toInt(input.orgId);
  const locationId = toInt(input.locationId);
  if (orgId == null || locationId == null) return null;
  // Without a loaded range there is no page to describe yet.
  if (!input.startDate || !input.endDate) return null;

  const descriptor = getPageDescriptor('journal_detail');

  const filters: Record<string, string> = {};
  if (input.categories && input.categories.length > 0) {
    // A scalar, because a filter carrying an array is page data trying to
    // arrive as context. The server treats it as a description of the slice.
    filters.categories = input.categories.join(',');
  }
  if (input.displayCurrency) filters.currency = input.displayCurrency;

  const singleDay = input.startDate === input.endDate ? input.startDate : null;
  const selectedDate = input.selectedDate || singleDay;

  const labelParts: (string | null)[] = [input.locationName || null];
  labelParts.push(singleDay ? singleDay : `${input.startDate} → ${input.endDate}`);
  if (selectedDate && selectedDate !== singleDay) labelParts.push(selectedDate);

  return {
    pageKey: 'journal_detail',
    orgId,
    propertyIds: [locationId],
    view: {
      route: input.route || '/journal-export/detail',
      mode: 'journal',
      startDate: input.startDate,
      endDate: input.endDate,
    },
    filters,
    selection: selectedDate
      ? { kind: 'journal_day', date: selectedDate, label: selectedDate }
      : null,
    dataRefs: [],
    capabilities: descriptor?.candidateCapabilities || [],
    labelParts,
  };
}

export default buildJournalDetailContext;
